import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { AxiosError } from "axios";
import isEmpty from "lodash/isEmpty";
import type { SubmitEvent } from "react";
import { useState } from "react";
import { przezmorzeApi } from "../../../api/api";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import DatePickerV2 from "../../components/datePicker/DatePickerV2";
import type { SelectedTag } from "../../components/tagSelect/TagSelect";
import TagSelect from "../../components/tagSelect/TagSelect";
import { CustomerNoteFields } from "../../customers/notes/CustomerNoteFields";
import { addCustomerNote } from "../../customers/notes/customerNotesApi";
import { useCustomerTagsByCategory } from "../../customers/useCustomerTagsByCategory";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import DictionaryAccordion from "./DictionaryAccordion";

export interface TagsState {
  readonly marketSegment: { readonly category: string; readonly value: string }[];
}

export function CustomerTagAssigment() {
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const [idsText, setIdsText] = useState("");
  const [tags, setTags] = useState<SelectedTag[]>([]);
  const [log, setLog] = useState("");
  const customerTagsByCategory = useCustomerTagsByCategory();
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState<string | null>(null);
  const [date, setDate] = useState<string>(dayjs().format(ISO_DATE_FORMAT));

  function reset() {
    setCustomerIds([]);
    setTags([]);
    setLog("");
    setNoteText("");
    setNoteType(null);
    setDate(dayjs().format(ISO_DATE_FORMAT));
  }

  async function handleNip2Id() {
    try {
      const response = await przezmorzeApi.post<
        {
          id: string;
          name: string;
          taxId: string;
        }[]
      >(`/customers/nip2id`, customerIds);
      setIdsText(response.data.map((it) => `${it.id}\t# ${it.taxId}: ${it.name}`).join("\n"));
      setCustomerIds(response.data.map((it) => it.id));
      setLog(`Znaleziono wszystkie NIPy: ${response.data.length}`);
    } catch (e) {
      console.error(e);
      if (e instanceof AxiosError) {
        setLog(e.response?.data.message ?? "");
      }
    }
  }

  async function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!confirm(`Przypisać tagi: \n Liczba klientów: ${customerIds.length}?`)) {
      return;
    }
    setLog(`Liczba klientów ${customerIds.length}:\n`);
    const note = isEmpty(noteText) ? undefined : { text: noteText, noteType };
    for (const id of customerIds) {
      setLog((log) => `${log}\n${id}: `);
      try {
        try {
          await przezmorzeApi.put(`/customers/${id}/tags`, tags);
          if (note) {
            await addCustomerNote(id, {
              category: "PRZEZMORZE",
              type: note.noteType ?? undefined,
              text: note.text,
              date,
            });
            setLog((log) => `${log} OK (tagi + notatka)`);
          } else {
            setLog((log) => `${log} OK (tagi)`);
          }
        } catch (e) {
          console.error(e);
          if (e instanceof AxiosError && e.status === 404) {
            setLog((log) => `${log} niepoprawny id - brak takiego klienta: ${e}`);
          }
        }
      } catch (e) {
        setLog((log) => `${log}${e}`);
      }
    }
    setLog((log) => `${log}\n\nSkończone`);
  }

  return (
    <DictionaryAccordion title="Przypisanie tagów i notatek do klientów" isLoading={false}>
      <form onSubmit={handleSubmit}>
        <CustomerNoteFields
          disabled={false}
          noteType={noteType}
          text={noteText}
          onNoteTypeChange={setNoteType}
          onTextChange={setNoteText}
        />
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem", alignItems: "flex-start" }}>
          <div style={{ display: "flex", gap: "1rem", flexDirection: "column", flex: 1 }}>
            <DatePickerV2
              label="Data notatki"
              fullWidth
              value={date ? dayjs(date, ISO_DATE_FORMAT) : null}
              onChange={(d) => setDate(d?.format(ISO_DATE_FORMAT) ?? "")}
            />
            <TagSelect
              availableTags={customerTagsByCategory}
              maxVisibleTags={2}
              tags={tags}
              label="Segment rynku"
              onChange={setTags}
            />
            <TextField
              multiline
              rows={12}
              fullWidth
              label="Wklej NIP lub ID klientów oddzielone spacją lub nową linią"
              value={idsText}
              onChange={(e) => {
                setIdsText(e.currentTarget.value);
                const ids = e.currentTarget.value
                  .split(/\s+/g)
                  .map((line) => line.replace(/#.*$/iu, ""))
                  .map((it) => it.trim())
                  .filter((it) => it.length > 0);
                setCustomerIds(ids);
              }}
              slotProps={{
                htmlInput: {
                  style: {
                    fontFamily: "monospace",
                    fontSize: 12,
                  },
                },
              }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <AsyncLoadingButton
                onClick={handleNip2Id}
                color="secondary"
                variant="contained"
                disabled={customerIds.length === 0}
              >
                NIP ➡ Id
              </AsyncLoadingButton>
              <Button type="submit" color="primary" variant="contained" disabled={customerIds.length === 0}>
                Przypisz do: {customerIds.length}
              </Button>
              <Button color="secondary" variant="contained" onClick={reset}>
                Reset
              </Button>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <pre
              style={{
                background: "#f5f5f5",
                padding: "1rem",
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #ccc",
                margin: 0,
              }}
            >
              {log}
            </pre>
          </div>
        </div>
      </form>
    </DictionaryAccordion>
  );
}
